import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ANIMAL_ICONS } from '../theme';
import { animalSounds } from '../utils/animalSounds';

/* ─── ANIMAL DISPLAY NAMES ─── */
const ANIMAL_NAMES = {
  fashion: 'THE LEOPARD',
  food: 'THE GAZELLE',
  explore: 'THE FALCON',
  souls: 'THE PHOENIX',
  sporty: 'THE GORILLA',
  success: 'THE STALLION',
  legacy: 'THE LION',
  art: 'THE CRANE',
};

/* ─── CSS KEYFRAMES — synced to roar audio peak ───
   Audio peak hits at ~200ms after roar phase starts.
   Shockwave rings launch at that exact moment.
   roarShake peaks at 200-400ms (synced with the audio climax).
   goldPulse (scale:1.1) beats at the bass thump timing. */
const CelebrationCSS = () => (
  <style>{`
    @keyframes roarShake {
      0%   { transform: scale(1) rotate(0deg); }
      5%   { transform: scale(1.04) rotate(-3deg); }
      12%  { transform: scale(1.1) rotate(4deg); }
      20%  { transform: scale(1.14) rotate(-5deg); }
      30%  { transform: scale(1.1) rotate(3deg); }
      40%  { transform: scale(1.06) rotate(-2deg); }
      55%  { transform: scale(1.03) rotate(1deg); }
      70%  { transform: scale(1.01) rotate(-1deg); }
      85%  { transform: scale(1) rotate(0deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
    @keyframes shockwavePulse {
      0%   { transform: translate(-50%, -50%) scale(0.15); opacity: 0.95; border-width: 3px; }
      30%  { opacity: 0.6; border-width: 2px; }
      50%  { transform: translate(-50%, -50%) scale(1.1); opacity: 0.35; border-width: 1.5px; }
      100% { transform: translate(-50%, -50%) scale(3.5); opacity: 0; border-width: 0.5px; }
    }
    @keyframes goldBeat {
      0%, 100% { box-shadow: 0 0 40px rgba(212,175,55,0.4); transform: scale(1); }
      25%  { box-shadow: 0 0 90px rgba(212,175,55,0.8), 0 0 160px rgba(212,175,55,0.3); transform: scale(1.1); }
      50%  { box-shadow: 0 0 60px rgba(212,175,55,0.5); transform: scale(1.02); }
      75%  { box-shadow: 0 0 80px rgba(212,175,55,0.7), 0 0 140px rgba(212,175,55,0.25); transform: scale(1.06); }
    }
    .roar-shake { animation: roarShake 1.0s ease-in-out; }
    .gold-beat  { animation: goldBeat 0.8s ease-in-out 2; }
  `}</style>
);

/* ─── SHOCKWAVE RING — timed to audio peak ─── */
const Shockwave = ({ delay }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      top: '50%', left: '50%',
      width: 180, height: 180,
      borderStyle: 'solid',
      borderColor: '#D4AF37',
      animation: `shockwavePulse 1.1s ${delay}s ease-out forwards`,
    }}
  />
);

/* ─── GOLD SPARK PARTICLE ─── */
const GoldSpark = ({ angle, dist, delay }) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: 3 + Math.random() * 3,
      height: 3 + Math.random() * 3,
      background: `rgba(212,175,55,${0.6 + Math.random() * 0.4})`,
    }}
    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
    animate={{
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      opacity: 0,
      scale: 0,
    }}
    transition={{ duration: 0.8 + Math.random() * 0.4, delay, ease: 'easeOut' }}
  />
);

export const VibeCelebration = ({ category, visible, onComplete, uploadedPreview }) => {
  const [phase, setPhase] = useState('idle');
  const icon = ANIMAL_ICONS[category];
  const name = ANIMAL_NAMES[category] || 'VIBE';

  const playSound = useCallback(() => {
    try { if (animalSounds[category]) animalSounds[category](); } catch (_) {}
  }, [category]);

  useEffect(() => {
    if (!visible) { setPhase('idle'); return; }

    // The celebration ONLY triggers after bar hits 100%.
    // Phase 1: Enter — icon zooms to center (0-400ms)
    setPhase('enter');

    // Phase 2: Roar — sound fires, shockwaves pulse in sync (at 400ms)
    // Audio peak at ~200ms into the roar = 600ms total
    // Shockwave rings pulse (scale:1.1) at that exact moment
    const t1 = setTimeout(() => { setPhase('roar'); playSound(); }, 400);

    // Phase 3: Reveal — cross-fade to uploaded photo (at 2000ms)
    const t2 = setTimeout(() => setPhase('reveal'), 2000);

    // Phase 4: Exit (at 3200ms)
    const t3 = setTimeout(() => setPhase('exit'), 3200);

    // Done (at 3800ms)
    const t4 = setTimeout(() => { if (onComplete) onComplete(); }, 3800);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [visible, playSound, onComplete]);

  if (!icon) return null;

  const sparks = Array.from({ length: 30 }, (_, i) => ({
    angle: (i / 30) * Math.PI * 2 + (Math.random() - 0.5) * 0.3,
    dist: 90 + Math.random() * 80,
    delay: 0.4 + Math.random() * 0.25,
  }));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{ background: '#000000' }}
          data-testid="vibe-celebration"
        >
          <CelebrationCSS />

          {/* ══ Radial gold aura — pure obsidian behind ══ */}
          <motion.div
            className="absolute pointer-events-none"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: phase === 'roar' ? 0.75 : phase === 'reveal' ? 0.35 : 0,
              scale: phase === 'roar' ? 2.2 : phase === 'reveal' ? 1.6 : 0,
            }}
            transition={{ duration: 0.5 }}
            style={{
              width: 320, height: 320, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.45) 0%, rgba(212,175,55,0.1) 45%, transparent 70%)',
            }}
          />

          {/* ══ SHOCKWAVE RINGS — pulse in sync with roar peak ══
              Ring 1: fires at roar start (0s) — arrives at peak ~200ms
              Ring 2: fires at 0.2s — hits peak synced with bass thump
              Ring 3: fires at 0.4s — synced with second bass hit
              Ring 4: fires at 0.65s — tail-end reverb */}
          {(phase === 'roar' || phase === 'reveal') && (
            <>
              <Shockwave delay={0} />
              <Shockwave delay={0.2} />
              <Shockwave delay={0.4} />
              <Shockwave delay={0.65} />
            </>
          )}

          {/* ══ Gold Spark Burst ══ */}
          {phase === 'roar' && (
            <div className="absolute" style={{ top: '50%', left: '50%' }}>
              {sparks.map((s, i) => <GoldSpark key={i} {...s} />)}
            </div>
          )}

          {/* ══ CENTER BEAST — appears ONLY after bar hits 100% ══ */}
          <motion.div
            className="relative flex flex-col items-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={
              phase === 'exit'
                ? { scale: 0.1, opacity: 0, y: -220 }
                : phase === 'reveal'
                ? { scale: 0.85, opacity: 1, y: 0 }
                : phase === 'roar'
                ? { scale: 1, opacity: 1, y: 0 }
                : { scale: 0.2, opacity: 0.4, y: 0 }
            }
            transition={
              phase === 'exit'
                ? { duration: 0.5, ease: 'easeIn' }
                : { type: 'spring', stiffness: 180, damping: 11 }
            }
          >
            {/* Icon circle — gold-beat pulses synced with audio */}
            <div className="relative">
              <motion.div
                className={`w-44 h-44 rounded-full overflow-hidden border-2 border-[#D4AF37] ${phase === 'roar' ? 'roar-shake gold-beat' : ''}`}
                style={{
                  boxShadow: phase === 'roar'
                    ? '0 0 60px rgba(212,175,55,0.6), 0 0 120px rgba(212,175,55,0.25)'
                    : '0 0 30px rgba(212,175,55,0.3)',
                }}
              >
                <div className="relative w-full h-full">
                  {/* Beast gold silhouette */}
                  <motion.img
                    src={icon.src}
                    alt={name}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      objectPosition: icon.pos,
                    }}
                    animate={{ opacity: phase === 'reveal' || phase === 'exit' ? 0 : 1 }}
                    transition={{ duration: 0.6 }}
                  />

                  {/* Uploaded photo cross-fade */}
                  {uploadedPreview && (
                    <motion.img
                      src={uploadedPreview}
                      alt="Your vibe"
                      className="absolute inset-0 w-full h-full object-cover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: (phase === 'reveal' || phase === 'exit') ? 1 : 0 }}
                      transition={{ duration: 0.6 }}
                    />
                  )}
                </div>
              </motion.div>
            </div>

            {/* Name text */}
            <motion.div
              className="text-center mt-5"
              initial={{ opacity: 0, y: 15 }}
              animate={{
                opacity: phase === 'roar' ? 1 : phase === 'reveal' ? 0.8 : 0,
                y: phase === 'roar' || phase === 'reveal' ? 0 : 15,
              }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <p className="font-['Cormorant_Garamond'] text-3xl text-[#D4AF37] font-light tracking-[0.2em]">
                {name}
              </p>
              <motion.p
                className="font-['Outfit'] text-xs mt-2 tracking-[0.15em] uppercase"
                animate={{ color: phase === 'reveal' ? 'rgba(255,255,255,0.5)' : 'rgba(212,175,55,0.5)' }}
                transition={{ duration: 0.4 }}
              >
                {phase === 'reveal' ? 'Vibe Captured' : 'Roars'}
              </motion.p>
            </motion.div>
          </motion.div>

          {/* Bottom accent */}
          <motion.div
            className="absolute bottom-16 left-1/2 -translate-x-1/2"
            initial={{ width: 0, opacity: 0 }}
            animate={{
              width: phase === 'roar' || phase === 'reveal' ? 180 : 0,
              opacity: phase === 'roar' || phase === 'reveal' ? 1 : 0,
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
